import React, { useEffect, useImperativeHandle, forwardRef, useMemo, useRef } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Markdown } from '@tiptap/markdown'
import { deltaToPmJson, pmJsonToDelta } from 'lib/value'
import type { DeltaJson, DeltaOp } from 'lib/value'
import { KnovMention } from './tiptap/MentionExtension'
import { createMentionSuggestion } from './tiptap/MentionSuggestion'
import { MemeExtension } from './tiptap/MemeExtension'
import { MemeSuggestionExtension } from './tiptap/MemeSuggestionExtension'
import { ImageExtension } from './tiptap/ImageExtension'
import { VideoExtension } from './tiptap/VideoExtension'
import { ActionPromptMarkExtension, ActionTokenExtension } from './tiptap/ActionTokenExtension'
import { ActionCommandSuggestionExtension } from './tiptap/ActionCommandSuggestionExtension'
import { ActionCommandAutocompleteItem } from './tiptap/actionCommandAutocompleteSource'
import {
    CollapsedContentExtension,
    collapsedContentPluginKey,
} from './tiptap/CollapsedContentExtension'
import { ClearMarksPlugin } from './tiptap/ClearMarksPlugin'
import { liveEditor } from './tiptap/editorLiveness'
import { CodeBlockExitPlugin } from './tiptap/CodeBlockExitPlugin'
import { IOSBottomEditorCaretPlugin } from './tiptap/IOSBottomEditorCaretPlugin'
import { PreserveHeadingMarkerInputRule } from './tiptap/PreserveHeadingMarkerInputRule'
import { PreserveNumberedTextInputRule } from './tiptap/PreserveNumberedTextInputRule'
import { LivePlaceholder, redrawPlaceholder } from './tiptap/livePlaceholder'
import { extractUrls, extractStreamEmbedConfigs } from 'components/shared/LinkPreview'
import { isBlockedExternalNavigation } from 'lib/externalNavigationPolicy'
import {
    BLOCKED_EXTERNAL_NAVIGATION_TOAST,
    getBlockedExternalNavigationUrl,
    markBlockedExternalNavigationsInPmJson,
    SafeExternalLink,
} from './tiptap/blockedExternalNavigation'
import toast from 'utils/toast'
import { debounce, uniq } from 'lodash'
import { generateFingerprint } from '../../utils/index'
import api from '../../api/api'
import { getKnovEmbedTargetKeys, resolveKnovEmbedLinks } from 'components/answers/knovEmbedLinks'
import {
    getCenterIndex,
    getSwiperRef,
    SWIPE_SPEED_FAST,
    SWIPE_SPEED_SLOW,
} from 'state/imperativeApis/swiperApi'
import useOpenRight from 'refactor/hooks/useOpenRight'
import {
    activateMentionFromReadOnlyContent,
    activateMemeFromReadOnlyContent,
} from './tiptap/readOnlyContentActivation'
import { isIOS, isMobile } from 'lib/device'
import {
    operationDiagnostics,
    type OperationTrace,
    type ComposerKind,
} from 'utils/operationDiagnostics'
import { memesEnabled } from 'lib/memesFeature'
import { setIMEComposing } from 'state/imperativeApis/editorApi'
import useStore from 'state/knovStore'
import { Editor, Extension, JSONContent } from '@tiptap/core'
import useUserHasFeature from '../../refactor/hooks/useUserHasFeature'
import useSearchHeaderHighlight from '../../refactor/hooks/useSearchHeaderHighlight'
import { v4 as uuid } from 'uuid'

// Import styles
import styles from './tiptap-editor.module.scss'
import fallbackMentionStyles from './mentions.module.scss'
import './tiptap/ActionToken.module.scss'
import './blocked-external-navigation.scss'

// JSON representation of a ProseMirror node from editor.getJSON()
interface PMNodeJSON {
    type: string
    attrs?: Record<string, unknown>
    content?: PMNodeJSON[]
    text?: string
    marks?: Array<{ type: string; attrs?: Record<string, unknown> }>
}

type CompositionEditorView = {
    composing?: boolean
    dom?: HTMLElement
    domObserver?: {
        flush: () => void
    }
}

const scheduleAndroidCompositionFlush = (
    view: CompositionEditorView,
    trace?: OperationTrace | null,
) => {
    if (typeof navigator === 'undefined' || !/\bAndroid\b/i.test(navigator.userAgent)) return

    // Samsung IMEs can keep updating the native composition DOM before
    // ProseMirror's mutation observer delivers the corresponding state update.
    // Flush in the next task so mention matching keeps pace with every update.
    window.setTimeout(() => {
        if (!view.composing || !view.dom?.isConnected) return
        trace?.mark('composition_flush_begin', { composing: true })
        view.domObserver?.flush()
        trace?.mark('composition_flush_end', { composing: !!view.composing })
    }, 0)
}

// Shape of a mention user/model item passed to the suggestion system
interface MentionItem {
    id: string
    content?: string
    name?: string
    value?: string
    link?: string
    type?: string
    denotationChar?: string
    [key: string]: unknown
}

// Shared selection range type (Quill-compatible)
interface SelectionRange {
    index: number
    length: number
}

// CSS module styles shape
interface MentionStylesMap {
    mention?: string
    userMention?: string
    modelMention?: string
    referenceMention?: string
    meme?: string
    memeBracket?: string
    title?: string
    answer?: string
    newAnswer?: string
    search?: string
    [key: string]: string | undefined
}

// Define the TiptapEditor component interface to match QuillEditor
interface TiptapEditorProps {
    defaultValue?: DeltaJson | string | null
    readOnly?: boolean
    placeholder?: string
    onChange?: (delta: DeltaJson, oldContents: DeltaJson | null, source: string) => void
    onTextChange?: (delta: DeltaJson, oldContents: DeltaJson | null, source: string) => void
    onSelectionChange?: (
        range: SelectionRange | null,
        oldRange: SelectionRange | null,
        source: string,
    ) => void
    onKeyDown?: (editor: Editor | null, event: KeyboardEvent) => boolean | void
    onFocus?: (editor: Editor) => void
    onBlur?: (editor: Editor) => void
    onEditorLoad?: (editor: TiptapEditorRef) => void
    onMentionClick?: (mentionId: string, event: Event, element: HTMLElement) => void
    style?: React.CSSProperties
    theme?: string | null
    lastKeyPressed?: string
    programmaticChangeRef?: React.MutableRefObject<boolean>
    // Mention system props
    users?: MentionItem[]
    models?: MentionItem[]
    refreshMentionUsers?: () =>
        | MentionItem[]
        | Promise<MentionItem[] | null | undefined>
        | null
        | undefined
    actionCommandItems?: ActionCommandAutocompleteItem[]
    getActionTokenReferenceMentions?: (
        attrs: Record<string, unknown>,
    ) => Array<Record<string, unknown>>
    getDefaultActionTokenAttrs?: (
        tag: string,
        item?: ActionCommandAutocompleteItem,
    ) => Record<string, unknown> | null | undefined
    onActionTokenPromptChange?: (payload: {
        attrs: Record<string, unknown>
        index: number
        promptContent: string
        committed?: boolean
        removed?: boolean
    }) => void
    readOnlyActionTokenPrompts?: boolean
    setAgentModel?: (model: string | null) => void
    // Link handling props
    setLinkPreviews?: (links: string[]) => void
    setEmbeds?: (embeds: unknown[], diagnostic?: OperationTrace | null) => void
    diagnosticComposer?: ComposerKind
    diagnosticComposerId?: string
    setEmbedsLoading?: (loading: boolean, promise?: Promise<unknown[]> | null) => void
    setStreamEmbeds?: (embeds: unknown[]) => void
    setStreamEmbedsLoading?: (loading: boolean) => void
    linkPreviewsFingerprint?: string
    embedsFingerprint?: string
    streamEmbedsFingerprint?: string
    answer?: {
        is_transcription?: boolean
        mp4_recording_url?: string
        recording_url?: string
        [key: string]: unknown
    }
    // CSS styles prop
    mentionStyles?: MentionStylesMap
    // Image/Video handling props
    imgHandler?: (event: {
        preventDefault: () => void
        stopPropagation: () => void
        target: { files: File[] }
    }) => void
    videoHandler?: (video: File) => void
    type?: string
    postHandler?: () => void
    // Collapsing / "more" UI
    collapseEnabled?: boolean
    collapseMaxChars?: number
    collapseInstanceId?: string
    collapseShowMoreControl?: boolean
    // Index signature required for ...otherProps spread pattern
    [key: string]: any // eslint-disable-line @typescript-eslint/no-explicit-any
}

// Define the ref interface
interface TiptapEditorRef {
    getEditor: () => Editor | null
    getContents: () => DeltaJson
    setContents: (delta: DeltaJson, source?: string) => void
    getText: () => string
    getLength: () => number
    getSelection: () => SelectionRange | null
    setSelection: (index: number, length?: number, source?: string) => void
    focus: () => void
    enable: (enabled?: boolean) => void
    hasFocus: () => boolean
    getJSON: () => JSONContent | undefined
    commands: Editor['commands'] | undefined
}

const HTTP_LINK_REGEX = /^https?:\/\//i
const MEME_LINK_PREFIX = '@v2quest_self_'

function isAllowedEditorLink(url: string | null | undefined): boolean {
    return (
        typeof url === 'string' && (HTTP_LINK_REGEX.test(url) || url.startsWith(MEME_LINK_PREFIX))
    )
}

function selectionIsInsideActionToken(state: Editor['state'] | null | undefined): boolean {
    if (!state?.doc) return false

    const { from, to } = state.selection
    let inside = false

    state.doc.descendants((node, pos) => {
        if (node.type.name !== 'actionTag') return true

        const contentFrom = pos + 1
        const contentTo = pos + node.nodeSize - 1
        if (from >= contentFrom && to <= contentTo) {
            inside = true
        }
        return false
    })

    return inside
}

const TiptapEditor = forwardRef<TiptapEditorRef, TiptapEditorProps>(
    (
        {
            defaultValue,
            type,
            readOnly = false,
            placeholder = 'Type here...',
            onChange,
            onTextChange,
            onSelectionChange,
            onKeyDown,
            onFocus,
            onBlur,
            onEditorLoad,
            onMentionClick,
            style,
            theme,
            lastKeyPressed,
            programmaticChangeRef,
            users,
            models,
            refreshMentionUsers,
            actionCommandItems,
            getActionTokenReferenceMentions,
            getDefaultActionTokenAttrs,
            onActionTokenPromptChange,
            readOnlyActionTokenPrompts = false,
            setAgentModel,
            setLinkPreviews,
            setEmbeds,
            diagnosticComposer,
            diagnosticComposerId,
            setEmbedsLoading,
            setStreamEmbeds,
            setStreamEmbedsLoading,
            linkPreviewsFingerprint,
            embedsFingerprint,
            streamEmbedsFingerprint,
            answer,
            mentionStyles,
            imgHandler,
            videoHandler,
            postHandler,
            collapseEnabled = false,
            collapseMaxChars = 400,
            collapseInstanceId,
            collapseShowMoreControl = true,
            ...otherProps
        },
        ref,
    ) => {
        const hasMobileShiftEnterFeature = useUserHasFeature('mobile_shift_enter_to_post')
        const { openRight } = useOpenRight()

        // Link handling refs - similar to CommonEditor
        const handleLinksRef = React.useRef(null)
        const handleKnovQuestLinksRef = React.useRef(null)
        const handleStreamLinksRef = React.useRef(null)
        const composerDiagnosticId = useRef<string | undefined>(undefined)
        const activeDiagnostic = useRef<OperationTrace | null>(null)
        const [inFlightDiagnostics] = React.useState(() => new WeakSet<OperationTrace>())
        const startDiagnostic = () => {
            if (!diagnosticComposer) return null
            composerDiagnosticId.current ||=
                diagnosticComposerId || operationDiagnostics.composerId()
            const trace = operationDiagnostics.start(
                diagnosticComposer,
                composerDiagnosticId.current,
            )
            activeDiagnostic.current = trace
            return trace
        }
        useEffect(
            () => () => {
                // An older request can still finish after a newer one or an unmount.
                // Keep its trace live so any resulting cache write remains visible.
                if (
                    activeDiagnostic.current &&
                    !inFlightDiagnostics.has(activeDiagnostic.current)
                ) {
                    activeDiagnostic.current.mark('cancelled')
                }
            },
            [],
        )

        // Track previous model mention state
        const previousHasModelMentionRef = React.useRef<boolean>(false)

        // ------------------------------------------------------------------
        // Live refs for users & models
        // ------------------------------------------------------------------
        const usersRef = useRef(users)
        const modelsRef = useRef(models)
        const refreshMentionUsersRef = useRef(refreshMentionUsers)
        const actionCommandItemsRef = useRef(actionCommandItems || [])
        const getActionTokenReferenceMentionsRef = useRef(getActionTokenReferenceMentions)
        // Read by LivePlaceholder whenever it draws; updated (and redrawn)
        // below once the editor exists.
        const placeholderRef = useRef(placeholder)

        // Get the search header highlight function
        const highlightSearchHeader = useSearchHeaderHighlight()

        // Keep refs updated when props change (runs on every render if array ref changes)
        useEffect(() => {
            usersRef.current = users
        }, [users])

        useEffect(() => {
            modelsRef.current = models
        }, [models])

        useEffect(() => {
            refreshMentionUsersRef.current = refreshMentionUsers
        }, [refreshMentionUsers])

        useEffect(() => {
            actionCommandItemsRef.current = actionCommandItems || []
        }, [actionCommandItems])

        useEffect(() => {
            getActionTokenReferenceMentionsRef.current = getActionTokenReferenceMentions
        }, [getActionTokenReferenceMentions])

        // Create debounced link handling functions
        React.useEffect(() => {
            // Create the debounced function for external links
            handleLinksRef.current = debounce(
                nonKnovLinks => {
                    const editorFingerprint = generateFingerprint(nonKnovLinks)
                    const hasChanges = linkPreviewsFingerprint !== editorFingerprint

                    // Only update if links have actually changed
                    if (hasChanges && setLinkPreviews) {
                        setLinkPreviews(uniq(nonKnovLinks))
                    }
                },
                500,
                {
                    leading: false,
                    trailing: true,
                },
            )

            // Create debounced function for KnovQuest links
            handleKnovQuestLinksRef.current = debounce(
                async knovEmbedLinks => {
                    const shortCircuit =
                        answer?.is_transcription ||
                        answer?.mp4_recording_url ||
                        answer?.recording_url
                    if (shortCircuit) return // don't do this for transcriptions

                    const knovEmbedTargetKeys = getKnovEmbedTargetKeys(knovEmbedLinks || [])
                    const editorFingerprint = generateFingerprint(knovEmbedTargetKeys)
                    const hasChanges = (embedsFingerprint || '') !== editorFingerprint

                    if (hasChanges) {
                        const diagnostic = knovEmbedLinks?.length ? startDiagnostic() : null
                        if (diagnostic) inFlightDiagnostics.add(diagnostic)
                        diagnostic?.mark('resolve_begin', {
                            link_count: knovEmbedLinks?.length || 0,
                        })
                        const embedPromise = resolveKnovEmbedLinks(knovEmbedLinks || [], {
                            fetchQuest: api.getQuest,
                            fetchAnswer: async id => {
                                diagnostic?.mark('post_fetch_begin')
                                const result = await api.getAnswer(id)
                                diagnostic?.mark('post_fetch_end')
                                return result
                            },
                            fetchAnonymousShareAnswer: token =>
                                api.getAnswerByAnonymousShareToken(token, diagnostic),
                        })
                            .then(embeds => {
                                embeds = embeds.filter(e => !!e)
                                diagnostic?.mark('resolve_end')
                                setEmbeds?.(embeds, diagnostic)
                                if (!embeds.length) diagnostic?.mark('failed')
                                return embeds
                            })
                            .catch(error => {
                                diagnostic?.mark('failed')
                                throw error
                            })
                            .finally(() => {
                                if (diagnostic) inFlightDiagnostics.delete(diagnostic)
                                setEmbedsLoading?.(false, null)
                            })

                        setEmbedsLoading?.(true, embedPromise)
                        return embedPromise
                    }
                },
                500,
                {
                    leading: false,
                    trailing: true,
                },
            )

            // Create debounced function for Stream links
            handleStreamLinksRef.current = debounce(
                async streamEmbedLinks => {
                    const shortCircuit =
                        answer?.is_transcription ||
                        answer?.mp4_recording_url ||
                        answer?.recording_url
                    if (shortCircuit) return // don't do this for transcriptions

                    const streamEmbeds = extractStreamEmbedConfigs(streamEmbedLinks || [])
                    const editorFingerprint = generateFingerprint(streamEmbeds, ',', 'id')
                    const hasChanges = streamEmbedsFingerprint !== editorFingerprint

                    if (hasChanges && setStreamEmbeds) {
                        setStreamEmbeds(streamEmbeds)
                    }
                    setStreamEmbedsLoading?.(false)
                },
                500,
                {
                    leading: false,
                    trailing: true,
                },
            )

            // Cleanup function
            return () => {
                if (handleLinksRef.current) {
                    handleLinksRef.current.cancel()
                }
                if (handleKnovQuestLinksRef.current) {
                    handleKnovQuestLinksRef.current.cancel()
                }
                if (handleStreamLinksRef.current) {
                    handleStreamLinksRef.current.cancel()
                }
            }
        }, [
            linkPreviewsFingerprint,
            embedsFingerprint,
            streamEmbedsFingerprint,
            answer,
            setLinkPreviews,
            setEmbeds,
            setStreamEmbeds,
            setStreamEmbedsLoading,
            diagnosticComposer,
        ])

        // Convert Delta JSON to ProseMirror JSON for initial content
        const initialContent = React.useMemo(() => {
            if (!defaultValue) {
                return { type: 'doc', content: [{ type: 'paragraph' }] }
            }

            // If it's already a string, use it directly
            if (typeof defaultValue === 'string') {
                return defaultValue
            }

            // If it's a Delta JSON object, convert it
            try {
                const pmJson = deltaToPmJson(defaultValue)

                // Ensure we have valid content
                if (!pmJson || !pmJson.content || pmJson.content.length === 0) {
                    return { type: 'doc', content: [{ type: 'paragraph' }] }
                }

                return readOnly ? markBlockedExternalNavigationsInPmJson(pmJson) : pmJson
            } catch (error) {
                console.warn('Failed to convert Delta to ProseMirror JSON:', error)
                return { type: 'doc', content: [{ type: 'paragraph' }] }
            }
        }, [defaultValue, readOnly])

        // Memoize extensions to prevent unnecessary re-creation
        const extensions = useMemo(() => {
            const currentMentionStyles = mentionStyles || fallbackMentionStyles
            const memeClass = currentMentionStyles?.meme

            // Helper function to process uploaded files through imgHandler
            const handleImageUpload = (file: File) => {
                if (imgHandler) {
                    // Create a synthetic event object similar to what QuillEditor creates
                    const syntheticEvent = {
                        preventDefault: () => {},
                        stopPropagation: () => {},
                        target: { files: [file] },
                    }
                    imgHandler(syntheticEvent)
                }
            }

            const handleVideoUpload = (file: File) => {
                if (videoHandler) {
                    videoHandler(file)
                }
            }

            return [
                StarterKit.configure({
                    link: false,
                    // Keep hardBreak enabled for desktop line breaks
                    hardBreak: {
                        // Only disable on mobile where we handle it manually
                        HTMLAttributes: {},
                    },
                    // Configure list behavior
                    bulletList: {
                        keepMarks: false,
                        keepAttributes: false,
                        HTMLAttributes: {
                            class: 'tiptap-bullet-list',
                        },
                    },
                    orderedList: {
                        keepMarks: false,
                        keepAttributes: false,
                        HTMLAttributes: {
                            class: 'tiptap-ordered-list',
                        },
                    },
                    listItem: {
                        HTMLAttributes: {
                            class: 'tiptap-list-item',
                        },
                    },
                }),
                SafeExternalLink.configure({
                    // All navigation is handled below so blocked links never retain a native path out.
                    openOnClick: false,
                    shouldAutoLink: url => isAllowedEditorLink(url),
                    isAllowedUri: (url, ctx) => {
                        return isAllowedEditorLink(url) && ctx.defaultValidate(url)
                    },
                    HTMLAttributes: {
                        target: '_blank',
                        rel: 'noopener noreferrer',
                    },
                }),
                Markdown.configure({
                    markedOptions: {
                        gfm: true,
                    },
                }),
                KnovMention.configure({
                    HTMLAttributes: {},
                    mentionClass: currentMentionStyles?.mention,
                    userMentionClass: currentMentionStyles?.userMention,
                    modelMentionClass: currentMentionStyles?.modelMention,
                    referenceMentionClass: currentMentionStyles?.referenceMention,
                    // Use getter functions to ensure suggestions always access the latest props data
                    // This prevents race conditions where the editor mounts before users/models are loaded
                    // The getter functions create a closure over the component scope, not the useMemo scope,
                    // so they always reference the current prop values when called
                    suggestion: createMentionSuggestion(
                        () => usersRef.current || [],
                        () => modelsRef.current || [],
                        setAgentModel,
                        attrs => getActionTokenReferenceMentionsRef.current?.(attrs) || [],
                        () => refreshMentionUsersRef.current?.(),
                    ),
                    onMentionClick: onMentionClick
                        ? onMentionClick
                        : readOnly
                        ? (mentionId: string, event: Event, element: HTMLElement) =>
                              activateMentionFromReadOnlyContent(
                                  mentionId,
                                  element,
                                  openRight,
                                  highlightSearchHeader,
                              )
                        : undefined,
                }),
                ActionCommandSuggestionExtension.configure({
                    getActionCommands: () => actionCommandItemsRef.current || [],
                    getDefaultActionTokenAttrs,
                    usePreviousTextAsDefaultPrompt: true,
                    wrapPreviousTextAsPrompt: false,
                }),
                MemeExtension.configure({
                    HTMLAttributes: {},
                    memeClass,
                    memeBracketClass: currentMentionStyles?.memeBracket,
                    onMemeClick: (memeContent: string, event: Event, element: HTMLElement) =>
                        activateMemeFromReadOnlyContent(memeContent, element, openRight),
                }),
                ...(!readOnly && memesEnabled() ? [MemeSuggestionExtension] : []),
                ImageExtension.configure({
                    HTMLAttributes: {},
                    onImageUpload: handleImageUpload,
                }),
                VideoExtension.configure({
                    HTMLAttributes: {},
                    onVideoUpload: handleVideoUpload,
                }),
                ActionPromptMarkExtension,
                ActionTokenExtension.configure({
                    onPromptContentChange: onActionTokenPromptChange,
                    readOnlyPrompts: readOnlyActionTokenPrompts,
                }),
                CollapsedContentExtension.configure({
                    enabled: !!collapseEnabled && !!readOnly,
                    maxChars: collapseMaxChars || 400,
                    maxLines: 10,
                    instanceId: collapseInstanceId || uuid(),
                    buttonId: 'expand-button',
                    showMoreControl: collapseShowMoreControl !== false,
                }),
                LivePlaceholder(() => placeholderRef.current),
                PreserveHeadingMarkerInputRule,
                PreserveNumberedTextInputRule,
                CodeBlockExitPlugin,
                IOSBottomEditorCaretPlugin.configure({ ios: isIOS }),
                // Add ClearMarksPlugin to handle cursor style boundaries after deletion
                ClearMarksPlugin,
            ]
        }, [
            setAgentModel,
            mentionStyles,
            imgHandler,
            videoHandler,
            onKeyDown,
            type,
            postHandler,
            readOnly,
            onMentionClick,
            openRight,
            getDefaultActionTokenAttrs,
            onActionTokenPromptChange,
            readOnlyActionTokenPrompts,
            collapseEnabled,
            collapseMaxChars,
            collapseInstanceId,
            collapseShowMoreControl,
        ])
        // Note: users, models and the placeholder are NOT in the dependencies array because we use
        // getter functions that always reference the current prop values when called. This prevents
        // unnecessary re-creation of extensions while ensuring suggestions always have access to the
        // latest data (and the extensions are only read when the editor is built anyway).

        const editor = useEditor({
            extensions,
            content: initialContent,
            editable: !readOnly,
            // Nothing this component renders reads the editor's state, and node
            // views render through EditorContent's own portals. A render per
            // transaction (every keystroke) only rebuilt the extension list and
            // pushed these options back into the view.
            shouldRerenderOnTransaction: false,
            editorProps: {
                attributes: {
                    class: 'tiptap-prose',
                    style: 'outline: none;',
                    'data-placeholder': placeholder,
                },
                // Use handleDOMEvents instead of handleKeyDown to get proper DOM events with modifier keys on iOS
                handleDOMEvents: {
                    pointerdown: () => {
                        activeDiagnostic.current?.mark('interaction')
                        return false
                    },
                    beforeinput: () => {
                        activeDiagnostic.current?.mark('interaction')
                        return false
                    },
                    compositionstart: (view, event) => {
                        try {
                            setIMEComposing(true)
                        } catch (e) {}
                        activeDiagnostic.current?.mark('composition_start', { composing: true })
                        scheduleAndroidCompositionFlush(
                            view as CompositionEditorView,
                            activeDiagnostic.current,
                        )
                        return false
                    },
                    compositionupdate: (view, event) => {
                        scheduleAndroidCompositionFlush(
                            view as CompositionEditorView,
                            activeDiagnostic.current,
                        )
                        return false
                    },
                    compositionend: (view, event) => {
                        try {
                            setIMEComposing(false)
                        } catch (e) {}
                        activeDiagnostic.current?.mark('composition_end', { composing: false })
                        return false
                    },
                    keydown: (view, event) => {
                        activeDiagnostic.current?.mark('interaction')
                        // Handle typing at the beginning of links to prevent link styling from bleeding
                        if (
                            event.key.length === 1 &&
                            !event.ctrlKey &&
                            !event.metaKey &&
                            !event.altKey
                        ) {
                            const { state } = view
                            const { selection } = state
                            const { $from } = selection

                            // Check if we're at the beginning of the document, start of a paragraph, or after a space
                            const isAtStart =
                                $from.pos === 1 || ($from.parentOffset === 0 && $from.pos > 1)

                            // Also check if we're right before a link (when typing at index 0 before a link)
                            let isBeforeLink = false
                            try {
                                const nodeAfter = $from.nodeAfter
                                if (nodeAfter && nodeAfter.marks) {
                                    isBeforeLink = nodeAfter.marks.some(
                                        mark => mark.type.name === 'link',
                                    )
                                }
                            } catch (e) {
                                // Ignore resolution errors
                            }

                            if (isAtStart || isBeforeLink) {
                                // Check if there are link marks at this position or stored marks
                                const currentMarks = $from.marks()
                                const storedMarks = state.storedMarks || []
                                const allMarks = [...currentMarks, ...storedMarks]

                                const hasLinkMark = allMarks.some(mark => mark.type.name === 'link')

                                if (hasLinkMark) {
                                    // Clear the link marks using ProseMirror transaction
                                    const linkMark = allMarks.find(
                                        mark => mark.type.name === 'link',
                                    )
                                    if (linkMark) {
                                        const tr = state.tr
                                        // Remove stored marks for future input
                                        tr.removeStoredMark(linkMark)
                                        // Apply the transaction
                                        view.dispatch(tr)
                                    }
                                }
                            }
                        }

                        // Handle space key specifically to clear link marks when creating separation
                        if (event.key === ' ') {
                            const { state } = view
                            const { selection } = state
                            const { $from } = selection

                            // Check if we have link marks that should be cleared
                            const currentMarks = $from.marks()
                            const storedMarks = state.storedMarks || []
                            const allMarks = [...currentMarks, ...storedMarks]

                            const linkMark = allMarks.find(mark => mark.type.name === 'link')

                            if (linkMark) {
                                // Check if we're at the end of a link (to prevent extending the link)
                                let isAtLinkEnd = false
                                try {
                                    const nodeBefore = $from.nodeBefore
                                    if (nodeBefore && nodeBefore.marks) {
                                        isAtLinkEnd = nodeBefore.marks.some(
                                            mark => mark.type.name === 'link',
                                        )
                                    }
                                } catch (e) {
                                    // Ignore resolution errors
                                }

                                if (isAtLinkEnd) {
                                    // Clear link marks to prevent extending the link with the space
                                    const tr = state.tr.removeStoredMark(linkMark)
                                    view.dispatch(tr)
                                }
                            }
                        }

                        if (onKeyDown && event.key === 'Enter') {
                            if (selectionIsInsideActionToken(view.state)) {
                                return false
                            }

                            // For search type, always handle Enter
                            // For other types, handle based on mobile/desktop rules
                            const shouldHandle =
                                type === 'search' ||
                                (isMobile && hasMobileShiftEnterFeature) ||
                                event.metaKey ||
                                event.ctrlKey

                            if (shouldHandle) {
                                // Call the handler with the proper DOM event
                                const result = onKeyDown(editor, event)
                                // Convert Quill convention (false = handled) to Tiptap convention (true = handled)
                                if (result === false) {
                                    return true // In Tiptap, returning true prevents default
                                }
                            }
                        }
                        return false // Allow default behavior
                    },
                    click: (view, event) => {
                        const target = event.target as HTMLElement
                        const linkElement = target.closest('a[href]') as HTMLAnchorElement

                        if (linkElement) {
                            const href = linkElement.getAttribute('href') || ''
                            const blockedUrl = getBlockedExternalNavigationUrl(linkElement)
                            if (blockedUrl) {
                                event.preventDefault()
                                event.stopPropagation()
                                toast.error(BLOCKED_EXTERNAL_NAVIGATION_TOAST, {
                                    kind: 'blocked-scam-link',
                                    testId: 'blocked-scam-link-toast',
                                })
                                return true
                            }

                            if (!href) {
                                event.preventDefault()
                                event.stopPropagation()
                                return true
                            }

                            const className = linkElement.getAttribute('class') || ''
                            const isMemeLink =
                                href.startsWith('@v2quest_self_') || className.includes('meme')

                            // Let MemeExtension handle meme links
                            if (isMemeLink) {
                                return false
                            }

                            // Handle regular links based on editor mode
                            event.preventDefault()
                            if (readOnly) {
                                // Open link in new tab when in read-only mode
                                window.open(linkElement.href, '_blank', 'noopener,noreferrer')
                            }
                            // In edit mode, prevent navigation (just prevent default)
                            return true
                        }

                        return false // Allow default behavior for non-links
                    },
                },
            },
            onUpdate: ({ editor: editorInstance, transaction }) => {
                // Check for model mention deletions only if this was a user action
                if (setAgentModel && !programmaticChangeRef?.current) {
                    try {
                        const pmJson = editorInstance.getJSON()
                        let currentHasModelMention = false

                        // Check if there are any model mentions in the document
                        const checkNode = (node: PMNodeJSON): boolean => {
                            if (node.type === 'mention' && node.attrs?.denotationChar === '@@') {
                                currentHasModelMention = true
                                return true
                            }
                            if (node.content && Array.isArray(node.content)) {
                                return node.content.some(checkNode)
                            }
                            return false
                        }

                        if (pmJson.content) {
                            ;(pmJson.content as PMNodeJSON[]).some(checkNode)
                        }

                        // Only clear agent model if we had a model mention before and now we don't
                        // This indicates the user deleted it
                        if (previousHasModelMentionRef.current && !currentHasModelMention) {
                            console.log(
                                '[TiptapEditor] Model mention deleted by user, clearing agent model',
                            )
                            setAgentModel(null)
                        }

                        // Update the previous state for next comparison
                        previousHasModelMentionRef.current = currentHasModelMention
                    } catch (error) {
                        console.warn('Failed to check for model mentions:', error)
                    }
                }

                if (onTextChange) {
                    try {
                        // Convert current content back to Delta for compatibility
                        const pmJson = editorInstance.getJSON()
                        const delta = pmJsonToDelta(pmJson)
                        const oldContents = null // TODO: track previous state if needed
                        onTextChange(delta, oldContents, 'user')

                        window.setTimeout(() => {
                            try {
                                const settledDelta = pmJsonToDelta(editorInstance.getJSON())
                                onTextChange(settledDelta, oldContents, 'user')
                            } catch (error) {
                                console.warn(
                                    'Failed to convert settled ProseMirror update to Delta:',
                                    error,
                                )
                            }
                        }, 0)
                    } catch (error) {
                        console.warn('Failed to convert ProseMirror to Delta:', error)
                    }
                }

                // Extract and handle links - similar to CommonEditor
                try {
                    const pmJson = editorInstance.getJSON()
                    const delta = pmJsonToDelta(pmJson)
                    const { knovEmbedLinks, nonKnovLinks, streamEmbedLinks } = extractUrls(delta)
                    const streamEmbeds = extractStreamEmbedConfigs(streamEmbedLinks || [])
                    const streamEmbedsChanged =
                        streamEmbedsFingerprint !== generateFingerprint(streamEmbeds, ',', 'id')

                    // Handle links and embeds
                    handleKnovQuestLinksRef?.current?.(knovEmbedLinks)
                    handleLinksRef?.current?.(nonKnovLinks)
                    setStreamEmbedsLoading?.(streamEmbeds.length > 0 && streamEmbedsChanged)
                    handleStreamLinksRef?.current?.(streamEmbedLinks)
                } catch (error) {
                    console.warn('Failed to process links:', error)
                }
            },
            onSelectionUpdate: ({ editor: editorInstance }) => {
                if (onSelectionChange) {
                    const selection = editorInstance.state.selection
                    const range = {
                        index: selection.from,
                        length: selection.to - selection.from,
                    }
                    onSelectionChange(range, null, 'user')
                }
            },
            onFocus: ({ editor: editorInstance }) => {
                if (onFocus) {
                    onFocus(editorInstance)
                }
            },
            onBlur: ({ editor: editorInstance }) => {
                if (onBlur) {
                    onBlur(editorInstance)
                }
            },
            onCreate: ({ editor: editorInstance }) => {
                // Initialize the model mention tracking
                try {
                    const pmJson = editorInstance.getJSON()
                    let hasModelMention = false

                    const checkNode = (node: PMNodeJSON): boolean => {
                        if (node.type === 'mention' && node.attrs?.denotationChar === '@@') {
                            hasModelMention = true
                            return true
                        }
                        if (node.content && Array.isArray(node.content)) {
                            return node.content.some(checkNode)
                        }
                        return false
                    }

                    if (pmJson.content) {
                        ;(pmJson.content as PMNodeJSON[]).some(checkNode)
                    }

                    previousHasModelMentionRef.current = hasModelMention
                } catch (error) {
                    console.warn('Failed to initialize model mention tracking:', error)
                }

                // Pass the ref (with compatibility methods) instead of raw editor
                // This ensures calling code gets expected Quill-like API
                if (onEditorLoad) {
                    // Create a temporary ref-like object with all the methods
                    const compatibleEditor = {
                        getEditor: () => liveEditor(editorInstance),
                        getContents: () => {
                            const live = liveEditor(editorInstance)
                            if (!live) return { ops: [] }
                            try {
                                const pmJson = live.getJSON()
                                return pmJsonToDelta(pmJson)
                            } catch (error) {
                                console.warn('Failed to get contents:', error)
                                return { ops: [] }
                            }
                        },
                        setContents: (delta: DeltaJson, source = 'api') => {
                            const live = liveEditor(editorInstance)
                            if (!live) return
                            try {
                                const pmJson = deltaToPmJson(delta)
                                live.commands.setContent(pmJson, { emitUpdate: false })

                                // If this is setting a model mention, ensure cursor is positioned correctly
                                const hasModelMention = delta?.ops?.some(
                                    (op: DeltaOp) =>
                                        (op?.insert as Record<string, unknown>)?.mention &&
                                        (op?.insert as Record<string, Record<string, unknown>>)
                                            ?.mention?.denotationChar === '@@',
                                )
                                if (hasModelMention) {
                                    // Position cursor after the mention and space
                                    setTimeout(() => {
                                        liveEditor(editorInstance)?.commands.focus('end', {
                                            scrollIntoView: false,
                                        })
                                    }, 0)
                                }
                            } catch (error) {
                                console.warn('Failed to set contents:', error)
                            }
                        },
                        getText: () => liveEditor(editorInstance)?.getText() || '',
                        getLength: () => liveEditor(editorInstance)?.state.doc.nodeSize || 0,
                        getSelection: () => {
                            const live = liveEditor(editorInstance)
                            if (!live) return null
                            const selection = live.state.selection
                            return {
                                index: selection.from,
                                length: selection.to - selection.from,
                            }
                        },
                        setSelection: (index: number, length = 0, source = 'api') => {
                            liveEditor(editorInstance)?.commands.setTextSelection({
                                from: index,
                                to: index + length,
                            })
                        },
                        focus: () =>
                            liveEditor(editorInstance)?.commands.focus(undefined, {
                                scrollIntoView: false,
                            }),
                        enable: (enabled = true) =>
                            liveEditor(editorInstance)?.setEditable(enabled),
                        hasFocus: () => liveEditor(editorInstance)?.isFocused || false,
                        getJSON: () => liveEditor(editorInstance)?.getJSON(),
                        get commands() {
                            return liveEditor(editorInstance)?.commands
                        },
                    }

                    onEditorLoad(compatibleEditor)
                }
            },
        })

        // Force a recompute when collapse options flip without a doc change
        useEffect(() => {
            if (!editor?.view) return
            try {
                editor.view.dispatch(
                    editor.state.tr.setMeta(collapsedContentPluginKey, {
                        type: 'setOptions',
                        options: {
                            enabled: !!collapseEnabled && !!readOnly,
                            maxChars: collapseMaxChars || 400,
                            maxLines: 10,
                            instanceId: collapseInstanceId || uuid(),
                            buttonId: 'expand-button',
                            showMoreControl: collapseShowMoreControl !== false,
                        },
                    }),
                )
            } catch (e) {}
        }, [
            editor,
            collapseEnabled,
            collapseMaxChars,
            collapseInstanceId,
            collapseShowMoreControl,
            readOnly,
        ])

        // Expose editor methods via ref to match QuillEditor API
        useImperativeHandle(
            ref,
            () => ({
                // Core editor access
                getEditor: () => liveEditor(editor),

                // Quill-compatible methods
                getContents: () => {
                    const live = liveEditor(editor)
                    if (!live) return { ops: [] }
                    try {
                        const pmJson = live.getJSON()
                        return pmJsonToDelta(pmJson)
                    } catch (error) {
                        console.warn('Failed to get contents:', error)
                        return { ops: [] }
                    }
                },

                setContents: (delta: DeltaJson, source = 'api') => {
                    const live = liveEditor(editor)
                    if (!live) return
                    try {
                        const pmJson = deltaToPmJson(delta)
                        live.commands.setContent(pmJson, { emitUpdate: false })

                        // If this is setting a model mention, ensure cursor is positioned correctly
                        const hasModelMention = delta?.ops?.some(
                            (op: DeltaOp) =>
                                (op?.insert as Record<string, unknown>)?.mention &&
                                (op?.insert as Record<string, Record<string, unknown>>)?.mention
                                    ?.denotationChar === '@@',
                        )
                        if (hasModelMention) {
                            // Position cursor after the mention and space
                            setTimeout(() => {
                                // The editor can be torn down before this fires.
                                liveEditor(editor)?.commands.focus('end', {
                                    scrollIntoView: false,
                                })
                            }, 0)
                        }
                    } catch (error) {
                        console.warn('Failed to set contents:', error)
                    }
                },

                getText: () => {
                    return liveEditor(editor)?.getText() || ''
                },

                getLength: () => {
                    return liveEditor(editor)?.state.doc.nodeSize || 0
                },

                getSelection: () => {
                    const live = liveEditor(editor)
                    if (!live) return null
                    const selection = live.state.selection
                    return {
                        index: selection.from,
                        length: selection.to - selection.from,
                    }
                },

                setSelection: (index: number, length = 0, source = 'api') => {
                    liveEditor(editor)?.commands.setTextSelection({
                        from: index,
                        to: index + length,
                    })
                },

                focus: () => {
                    liveEditor(editor)?.commands.focus(undefined, { scrollIntoView: false })
                },

                enable: (enabled = true) => {
                    liveEditor(editor)?.setEditable(enabled)
                },

                hasFocus: () => {
                    return liveEditor(editor)?.isFocused || false
                },

                // Tiptap-specific methods
                getJSON: () => liveEditor(editor)?.getJSON(),
                // A getter, not a value: reading `.commands` off a destroyed
                // editor throws, and this memo can outlive the teardown.
                get commands() {
                    return liveEditor(editor)?.commands
                },
            }),
            [editor],
        )

        // A placeholder that changes after the editor exists (a direct
        // message's composer learning the other person's name) is drawn at once.
        useEffect(() => {
            if (placeholderRef.current === placeholder) return
            placeholderRef.current = placeholder
            redrawPlaceholder(editor)
        }, [editor, placeholder])

        // Handle enable/disable when readOnly changes
        useEffect(() => {
            if (editor) {
                editor.setEditable(!readOnly)

                // Update placeholder when it changes
                editor.setOptions({
                    editorProps: {
                        attributes: {
                            class: 'tiptap-prose',
                            style: 'outline: none;',
                            'data-placeholder': placeholder,
                        },
                    },
                })

                // Add development helpers for testing media functionality
                if (process.env.NODE_ENV === 'development') {
                    // @ts-expect-error - dev-only test helpers attached to window
                    window.testTiptapImage = () => {
                        console.log('Adding test image...')
                        editor.commands.setImage({
                            src: 'https://via.placeholder.com/300x200/0066cc/ffffff?text=Test+Image',
                            alt: 'Test image',
                        })
                    }
                    // @ts-expect-error - dev-only test helpers attached to window
                    window.testTiptapVideo = () => {
                        console.log('Adding test video...')
                        editor.commands.setVideo({
                            src: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
                            controls: true,
                        })
                    }

                    // Only log helper info in development
                    // console.log('💡 TiptapEditor media helpers available:')
                    // console.log('  - testTiptapImage() - add a test image')
                    // console.log('  - testTiptapVideo() - add a test video')
                    // console.log('  - Try drag & drop images/videos into the editor!')
                    // console.log('  - Try pasting images from clipboard!')
                }
            }
        }, [editor, readOnly, mentionStyles, placeholder])
        // Helper function to get the appropriate CSS class based on type
        const getTypeClass = (type?: string) => {
            const currentMentionStyles = mentionStyles || fallbackMentionStyles

            switch (type) {
                case 'title':
                    return currentMentionStyles.title || ''
                case 'answer':
                    return currentMentionStyles.answer || ''
                case 'new-answer':
                    return currentMentionStyles.newAnswer || ''
                case 'search':
                    return currentMentionStyles.search || ''
                default:
                    return currentMentionStyles.answer || '' // Default to answer styling
            }
        }

        return (
            <div
                style={style}
                {...otherProps}
                className={`${styles.tiptapEditorContainer} tiptap-editor-container ${getTypeClass(
                    type,
                )} ${collapseEnabled && readOnly ? 'knov-collapse-active' : ''} ${
                    readOnly ? 'readonly' : ''
                }`}
                data-mention-editor="true"
            >
                <EditorContent
                    editor={editor}
                    className={`${styles.editorContent} tiptap-editor-content`}
                />
            </div>
        )
    },
)

TiptapEditor.displayName = 'TiptapEditor'

export default TiptapEditor
